import { useEffect, useState } from 'react'
import { PRODUCT_NAME } from '../brand.js'
import { beginTotpEnrollment, confirmTotpEnrollment } from './api.js'
import { encodeQr } from './qr.js'

/**
 * Two Factor Setup — QR code, manual key, and confirmation.
 *
 * Authenticator app only. Email and WhatsApp 2FA are recorded decisions
 * (DESIGN-DECISIONS §0.3) but depend on SMTP and an authentication-category
 * template, neither of which exists, so neither is offered here.
 */

type Props = {
  onComplete: () => void
  onCancel: () => void
}

function QrCode({ text }: { text: string }): React.JSX.Element | null {
  const qr = encodeQr(text)
  // A failure degrades to the manual key below, which is always shown, rather
  // than blocking enrollment.
  if (!qr) return null

  const quiet = 2
  const dim = qr.size + quiet * 2

  return (
    <svg viewBox={`0 0 ${dim} ${dim}`} role="img" aria-label="Two factor setup QR code">
      {/*
        Fixed white backing and black modules, deliberately NOT themed. A QR
        scanner needs real contrast, and a code that inverted in dark mode would
        fail to scan. `white` and `currentColor` are theme-agnostic keywords, so
        the §11 colour lint permits them.
      */}
      <rect width={dim} height={dim} fill="white" />
      <g fill="currentColor" color="black">
        {qr.modules.map((row, r) =>
          row.map((on, c) =>
            on ? <rect key={`${r}-${c}`} x={c + quiet} y={r + quiet} width={1} height={1} /> : null,
          ),
        )}
      </g>
    </svg>
  )
}

export function TwoFactorSetup({ onComplete, onCancel }: Props): React.JSX.Element {
  const [secret, setSecret] = useState<string | null>(null)
  const [otpauthUrl, setOtpauthUrl] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void (async () => {
      const result = await beginTotpEnrollment()
      setLoading(false)
      switch (result.kind) {
        case 'success':
          setSecret(result.secret)
          setOtpauthUrl(result.otpauthUrl)
          return
        case 'alreadyEnabled':
          setError('Two Factor Authentication Is Already Enabled On This Account.')
          return
        case 'unauthenticated':
          setError('Your Session Has Ended. Please Sign In Again.')
          return
        case 'networkError':
          setError('Could Not Start Setup. Check Your Connection And Try Again.')
          return
      }
    })()
  }, [])

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setError(null)

    const result = await confirmTotpEnrollment(code)
    setBusy(false)

    switch (result.kind) {
      case 'success':
        onComplete()
        return
      case 'invalidCode':
        // The backend only flips totp_enabled after a code verifies, so a
        // failure here means the authenticator is not yet correctly paired.
        // Saying so is more useful than "invalid code".
        setError('That Code Is Not Correct. Check The Time On Your Device And Try Again.')
        setCode('')
        return
      case 'unauthenticated':
        setError('Your Session Has Ended. Please Sign In Again.')
        return
      case 'networkError':
        setError('Could Not Reach The Server. Check Your Connection And Try Again.')
        return
    }
  }

  return (
    <div className="auth">
      <div className="auth__card auth__card--wide">
        <p className="auth__brand">{PRODUCT_NAME}</p>
        <h1 className="auth__title">Set Up Two Factor Authentication</h1>
        <p className="auth__subtitle">
          Scan the code below with an authenticator app such as Google Authenticator,
          1Password or Authy.
        </p>

        {/*
          THE WARNING IS SHOWN BEFORE ENROLLMENT COMPLETES, not after.
          FOUNDATION deliberately has no TOTP recovery codes, so a lost
          authenticator needs an administrator. Telling someone that after they
          have already enrolled is telling them too late to act on it.
        */}
        <div className="auth__message auth__message--warning">
          <p className="auth__message-title">There Are No Recovery Codes</p>
          <p>
            If you lose access to your authenticator app you will not be able to sign in
            on your own. An administrator will have to reset two factor authentication for
            you.
          </p>
          <p>
            Keep a backup of your authenticator, or store the setup key somewhere safe.
          </p>
        </div>

        {error !== null && (
          <div className="auth__message auth__message--error" role="alert">
            <p>{error}</p>
          </div>
        )}

        {loading && (
          <div className="auth__message auth__message--info">
            <p>Preparing Setup</p>
          </div>
        )}

        {secret !== null && otpauthUrl !== null && (
          <>
            <div className="auth__qr">
              <QrCode text={otpauthUrl} />
            </div>

            <p className="auth__label">Or Enter This Key Manually</p>
            <p className="auth__manual-key">{secret}</p>

            <ol className="auth__steps">
              <li>Scan the code or enter the key in your authenticator app.</li>
              <li>Enter the six digit code it shows below.</li>
              <li>Two factor authentication turns on only after the code is confirmed.</li>
            </ol>

            <form onSubmit={submit}>
              <div className="auth__field">
                <label className="auth__label" htmlFor="setup-code">
                  Confirmation Code
                </label>
                <input
                  id="setup-code"
                  className="auth__input auth__input--code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>

              <button type="submit" className="auth__button" disabled={busy || code.length < 6}>
                {busy ? 'Verifying' : 'Confirm And Turn On'}
              </button>
            </form>
          </>
        )}

        <div className="auth__links">
          <button type="button" className="auth__link" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
